import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { locationTypes } from '../../constants/statusOptions';
import { createLocationForSite } from '../../services/locationService';
import { getProjects } from '../../services/projectService';

interface ProjectOption {
  ProjectKey: string;
  ProjectId: string;
  ProjectName: string;
}

interface NewLocationForSiteDialogProps {
  open: boolean;
  siteKey: string;
  onClose: () => void;
  onCreated: () => void;
}

export const NewLocationForSiteDialog = ({
  open, siteKey, onClose, onCreated,
}: NewLocationForSiteDialogProps) => {
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [nameEditedByUser, setNameEditedByUser] = useState(false);
  const [locationType, setLocationType] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && siteKey) {
      // Load projects for this site
      getProjects({ pageSize: 500, sortBy: 'ProjectName', sortDir: 'asc' })
        .then((res) => {
          // Filter to projects at this site
          const siteProjects = res.data
            .filter((p) => p.SiteKey === siteKey)
            .map((p) => ({ ProjectKey: p.ProjectKey, ProjectId: p.ProjectId, ProjectName: p.ProjectName }));
          setProjects(siteProjects);
          if (siteProjects.length === 1) setSelectedProject(siteProjects[0]);
        })
        .catch(() => {});
    }
  }, [open, siteKey]);

  const isValid = locationId.trim() !== '' && locationName.trim() !== '' && locationType !== '' && !!selectedProject;

  const resetForm = () => {
    setLocationId(''); setLocationName(''); setLocationType('');
    setNameEditedByUser(false);
    setSelectedProject(null); setError('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleSubmit = async () => {
    if (!isValid || !selectedProject) return;
    setSaving(true);
    setError('');
    try {
      await createLocationForSite(siteKey, {
        LocationId: locationId.trim(),
        LocationName: locationName.trim(),
        LocationType: locationType,
        ProjectKey: selectedProject.ProjectKey,
      });
      resetForm();
      onCreated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to create location.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Location</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete size="small" options={projects}
            getOptionLabel={(opt) => `${opt.ProjectId} — ${opt.ProjectName}`}
            value={selectedProject}
            onChange={(_e, val) => setSelectedProject(val)}
            isOptionEqualToValue={(opt, val) => opt.ProjectKey === val.ProjectKey}
            renderInput={(params) => (
              <TextField {...params} label="Project" required
                helperText="Location will belong to this project" />
            )} />
          <TextField size="small" label="Location ID" required fullWidth
            value={locationId} onChange={(e) => {
              const v = e.target.value; setLocationId(v);
              if (!nameEditedByUser) setLocationName(v);
            }} />
          <TextField size="small" label="Location Name" required fullWidth
            value={locationName} onChange={(e) => { setLocationName(e.target.value); setNameEditedByUser(true); }} />
          <TextField size="small" label="Location Type" required fullWidth select
            value={locationType} onChange={(e) => setLocationType(e.target.value)}>
            {locationTypes.map((lt) => <MenuItem key={lt} value={lt}>{lt}</MenuItem>)}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || saving}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}>
          {saving ? 'Creating...' : 'Create Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
